import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Flex, Form, Modal, Select, Typography } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  applicationsAccessCreateMutation,
  applicationsApplicationsAccessListOptions,
  applicationsApplicationsAccessListQueryKey,
  organizationsAccessListInfiniteOptions,
  organizationsAccessListOptions,
  organizationsAccessRolesCreateMutation,
  organizationsRolesListOptions,
  type ApplicationAccessList,
  type OrganizationsAccessListData,
  type OrganizationUsers,
  type PaginatedOrganizationUsersList
} from 'src/api/main'
import { InfiniteSelect } from 'src/components'
import { mergeManagedRoleIds } from 'src/features/users/utils'
import { useInvalidatePredicateQueries } from 'src/hooks'
import type { OptionTransformType } from 'src/utils'
import {
  composeDisplayName,
  normalizeListResults,
  useShowApiErrors
} from 'src/utils'

import { buildAppAccessApplications } from '../utils/appAccess'

const { Text } = Typography

const PER_PAGE_MAX = 200

type Props = {
  open: boolean
  applicationId: number
  organizationId: number
  /** Row to manage (role change / removal). Null → grant a new member. */
  manageTarget?: ApplicationAccessList | null
  onClose: () => void
}

type FormValues = {
  /** OrganizationAccess id of the member (not the User id). Grant mode only. */
  accessId?: number
  roleId?: number
}

/**
 * Grant a workspace member access to this app, or change the app role on an
 * existing access. `applicationsAccessCreate` is declarative — it sets the
 * member's full app list — so the member's other accesses always ride along
 * in the payload. App-scoped role ids are committed via the same roles-merge
 * convention as the user-detail App access tab. Removal lives in the users
 * table's row dropdown (`useAppAccessRemoval`), not here.
 */
export const AppAccessModal = ({
  open,
  applicationId,
  organizationId,
  manageTarget = null,
  onClose
}: Props) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const [form] = Form.useForm<FormValues>()

  const isManage = manageTarget != null
  // Grant mode picks the member from a paginated select, which hands back the
  // whole record — the merge below needs its org-level roles and user id.
  const [pickedMember, setPickedMember] = useState<OrganizationUsers | null>(
    null
  )

  // In manage mode the same endpoint resolves just the target member's
  // OrganizationAccess record (it carries the org-level roles for the merge).
  const manageMemberQuery = useQuery({
    ...organizationsAccessListOptions({
      query: {
        organization: organizationId,
        user: manageTarget?.user.id ?? 0,
        per_page: 10
      }
    }),
    enabled: open && isManage && organizationId > 0
  })
  // Some list endpoints have pagination disabled and return a bare array
  // instead of `{ results }`; accept either shape.
  const manageMembers = useMemo(
    () => normalizeListResults(manageMemberQuery.data),
    [manageMemberQuery.data]
  )

  const rolesQuery = useQuery({
    ...organizationsRolesListOptions({
      query: {
        application: applicationId,
        organization: organizationId,
        per_page: PER_PAGE_MAX
      }
    }),
    enabled: open && applicationId > 0
  })
  const appRoles = useMemo(
    () => normalizeListResults(rolesQuery.data),
    [rolesQuery.data]
  )

  const accessIdWatch = Form.useWatch('accessId', form)
  // Pairing the captured record with the live field value keeps the two from
  // drifting apart when the form is reset behind the select.
  const resolvedMember = isManage
    ? (manageMembers[0] ?? null)
    : pickedMember?.id === accessIdWatch
      ? pickedMember
      : null

  // The create endpoint sets the member's complete app list, so their current
  // accesses must ride along — fetch them as soon as the member is known.
  const userAccessQuery = useQuery({
    ...applicationsApplicationsAccessListOptions({
      query: {
        organization: organizationId,
        user: resolvedMember?.user.id ?? 0,
        per_page: PER_PAGE_MAX
      }
    }),
    enabled: open && resolvedMember != null
  })

  useEffect(() => {
    if (!open) return
    if (manageTarget) {
      form.setFieldsValue({ roleId: manageTarget.roles[0]?.id })
    } else {
      form.resetFields()
    }
  }, [open, manageTarget, form])

  const { mutateAsync: setAppAccess, isPending: isSettingAccess } = useMutation(
    { ...applicationsAccessCreateMutation() }
  )
  const { mutateAsync: setRoles, isPending: isSettingRoles } = useMutation({
    ...organizationsAccessRolesCreateMutation()
  })

  const handleClose = () => {
    form.resetFields()
    setPickedMember(null)
    onClose()
  }

  const handleSubmit = async (values: FormValues) => {
    const member = resolvedMember
    // Accept the bare-array shape too, but keep `undefined` while the query is
    // unresolved so the guard below still fires before the data loads.
    const existingAccess =
      userAccessQuery.data == null
        ? undefined
        : normalizeListResults(userAccessQuery.data)
    // Fail loud rather than send a partial list that would revoke the
    // member's other app accesses.
    if (!member || !existingAccess) return

    const selectedRole = appRoles.find((r) => r.id === values.roleId) ?? null

    try {
      await setAppAccess({
        path: { organization_id: organizationId },
        body: {
          users: [member.id],
          applications: buildAppAccessApplications(
            existingAccess,
            applicationId,
            { role: selectedRole?.name ?? null }
          )
        }
      })

      // Role ids live on the OrganizationAccess record; replace only the
      // roles this app owns and keep everything else the member has.
      const managed = new Set(appRoles.map((r) => r.id))
      await setRoles({
        path: { id: member.id },
        body: {
          roles: mergeManagedRoleIds(
            member.roles.map((r) => r.id),
            managed,
            selectedRole ? [selectedRole.id] : []
          )
        }
      })

      await invalidateQueries([applicationsApplicationsAccessListQueryKey()])
      message.success(
        isManage
          ? t('workspace.apps.usersTab.updateSuccess')
          : t('workspace.apps.usersTab.grantSuccess')
      )
      handleClose()
    } catch (error) {
      showApiErrors(error)
    }
  }

  const memberOptions = ({ options }: OptionTransformType<OrganizationUsers>) =>
    options.map((m) => {
      const name = composeDisplayName(
        m.user.first_name,
        m.user.last_name,
        m.user.email
      )

      return {
        value: m.id,
        label: name === m.user.email ? name : `${name} (${m.user.email})`,
        member: m
      }
    })

  const roleOptions = appRoles.map((r) => ({ value: r.id, label: r.name }))

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={
        isManage
          ? t('workspace.apps.usersTab.manageAccessTitle')
          : t('workspace.apps.usersTab.grantAccessTitle')
      }
      onOk={() => form.submit()}
      okText={isManage ? t('save') : t('workspace.apps.usersTab.grantAccess')}
      cancelText={t('cancel')}
      confirmLoading={isSettingAccess || isSettingRoles}
      okButtonProps={{
        disabled: resolvedMember != null && userAccessQuery.isLoading
      }}
      destroyOnHidden
    >
      <Form<FormValues>
        form={form}
        layout="vertical"
        onFinish={(values) => void handleSubmit(values)}
      >
        {isManage ? (
          <Form.Item label={t('workspace.apps.usersTab.memberLabel')}>
            <Flex vertical style={{ lineHeight: 1.3 }}>
              <Text strong>
                {composeDisplayName(
                  manageTarget.user.first_name,
                  manageTarget.user.last_name,
                  manageTarget.user.email
                )}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {manageTarget.user.email}
              </Text>
            </Flex>
          </Form.Item>
        ) : (
          <Form.Item
            name="accessId"
            label={t('workspace.apps.usersTab.memberLabel')}
            rules={[
              {
                required: true,
                message: t('workspace.apps.usersTab.memberRequired')
              }
            ]}
          >
            <InfiniteSelect<
              OrganizationsAccessListData,
              PaginatedOrganizationUsersList,
              OrganizationUsers
            >
              getQueryFunction={organizationsAccessListInfiniteOptions}
              baseOptions={{ query: { organization: organizationId } }}
              getOptions={memberOptions}
              placeholder={t('workspace.apps.usersTab.memberPlaceholder')}
              onChange={(_, option) =>
                setPickedMember(
                  (option as { member?: OrganizationUsers })?.member ?? null
                )
              }
            />
          </Form.Item>
        )}
        <Form.Item name="roleId" label={t('workspace.apps.usersTab.roleLabel')}>
          <Select
            allowClear
            showSearch={{ optionFilterProp: 'label' }}
            loading={rolesQuery.isLoading}
            options={roleOptions}
            placeholder={t('workspace.apps.usersTab.rolePlaceholder')}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
